import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useMemo,
  useImperativeHandle,
  useRef,
  useState,
  FocusEvent,
  ReactNode,
} from 'react';
import { ChangeHandler } from 'react-hook-form';
import { Config } from '../../helpers/config';
import { IconProps } from '../Icons';

import './TextField.css';

type TextFieldProps = {
  placeholder: string;
  isPlaceholderItalic?: boolean;
  value?: string;
  onChange?: ChangeHandler | ((event: ChangeEvent<HTMLInputElement>) => void);
  onBlur?: ChangeHandler;
  onValueChange?: (newVal: string) => void;
  type?: React.HTMLInputTypeAttribute;
  name?: string;
  error?: string;
  node?: ReactNode;
  Icon?: React.FC<IconProps & { color?: string }>;
  onIconClick?: () => void;
  autocomplete?: 'current-password' | 'new-password';
};

export const TextField: React.ForwardRefExoticComponent<
  TextFieldProps & React.RefAttributes<HTMLInputElement>
> = forwardRef(
  (
    {
      name,
      error,
      placeholder,
      value,
      onValueChange,
      onChange,
      onBlur,
      Icon,
      node,
      onIconClick,
      autocomplete,
      isPlaceholderItalic,
      type = 'text',
    },
    ref
  ) => {
    const [iconColor, setIconColor] = useState<string>(Config.black);

    const onInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);
        onValueChange?.(e.target.value);
      },
      [onChange, onValueChange]
    );

    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current!);

    const inputClass = useMemo(() => {
      return `text-field--input ${error ? 'text-field--input-error' : ''}`;
    }, [error]);

    const labelClass = useMemo(() => {
      return `text-field--label ${error ? 'text-field--label-error' : ''} ${
        isPlaceholderItalic ? 'text-field--label-italic' : ''
      }`;
    }, [error, isPlaceholderItalic]);

    const onInputFocus = useCallback(() => {
      setIconColor(Config.primaryBlue);
    }, []);

    const onInputBlur = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        setIconColor(Config.black);
        onBlur?.(e);
      },
      [onBlur]
    );

    return (
      <div className="text-field--container">
        <div className="text-field--group">
          <input
            ref={inputRef}
            onBlur={onInputBlur}
            value={value}
            onFocus={onInputFocus}
            onChange={onInputChange}
            type={type}
            id={`input-field-${name}`}
            name={name}
            className={inputClass}
            placeholder={placeholder}
            autoComplete={autocomplete}
          />
          <label htmlFor={`input-field-${name}`} className={labelClass}>
            {placeholder}
          </label>
          {node && (
            <div
              onClick={onIconClick}
              className="text-field--icon-container text-field--icon-end"
            >
              {node}
            </div>
          )}
          {Icon && (
            <div
              onClick={onIconClick}
              className="text-field--icon-container text-field--icon-end"
            >
              <Icon color={iconColor} />
            </div>
          )}
        </div>
        {error && <p className="text-field--error-message">{error}</p>}
      </div>
    );
  }
);
